import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import algoliasearch from 'algoliasearch/lite';
import {
  Configure,
  Hits,
  Index,
  InstantSearch,
  Pagination,
  SearchBox,
} from 'react-instantsearch-hooks-web';

import { baseAppId } from 'settings';
import i18n from 'i18n-react';

import Hit from '../Hit';
import NoResultsWrapper from '../NoResults';

// higher order component to bind the onSelect function and the proper type to the Hit component.
// Required to prevent: no-unstable-nested-components eslint (which prevents unnecessary renders).
const getPreparedHitComponent = (onSelect, type) => props =>
  <Hit {...props} type={type} onSelect={onSelect} />;

/**
 * A specific implementation/configuration that allows for searching a desired subapp.
 *
 * @param {string} algoliaId An algolia search id. See algolia dashboard.
 * @param {string} algoliaSearchKey An algolia search key. See algolia dashboard.
 * @param {Function} hitComponent A function that returns a Hit component. Must accept only props.
 * @returns
 */
const IndexSearch = ({ algoliaId, algoliaSearchKey, onHide }) => {
  const searchClient = algoliasearch(algoliaId, algoliaSearchKey);

  const PreparedSalesCompanionHit = useMemo(
    () => getPreparedHitComponent(onHide, 'sales_companion_items'),
    [onHide],
  );

  const PreparedPagesHit = useMemo(
    () => getPreparedHitComponent(onHide, 'pages'),
    [onHide],
  );

  return (
    <InstantSearch searchClient={searchClient}>
      <SearchBox autoFocus placeholder={i18n.translate('searchView.search')} />
      <Index indexName="pages">
        <Configure filters={`app_id = ${baseAppId} AND chapter_id = 3826`} hitsPerPage={8} />
        <h3>Pages</h3>
        <div className="search__container">
          <NoResultsWrapper>
            <Hits hitComponent={PreparedPagesHit} />
          </NoResultsWrapper>
          <Pagination />
        </div>
      </Index>
      <Index indexName="sales_companion_items">
        <Configure filters={`app_id = ${baseAppId}`} hitsPerPage={8} />
        <h3>Resources</h3>
        <div className="search__container">
          <NoResultsWrapper>
            <Hits hitComponent={PreparedSalesCompanionHit} />
          </NoResultsWrapper>
          <Pagination />
        </div>
      </Index>
    </InstantSearch>
  );
};

IndexSearch.propTypes = {
  algoliaId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default IndexSearch;
